import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AuthProvider } from '@/hooks/useAuth'
import { ToastProvider } from '@/components/ui/Toast'
import App from './App'
import './styles/index.css'

// Auto-recover from stale lazy chunks after a deploy. When a dynamically
// imported route chunk fails to load — its hashed filename was removed by a
// newer deploy, or a mid-deploy request got a stale response — Vite fires
// `vite:preloadError`. Reload once to pull the fresh index.html + current chunk
// names. The 10s guard prevents a reload loop when the failure is persistent
// (e.g. a poisoned CDN edge, which needs a cache purge, not a reload).
window.addEventListener('vite:preloadError', () => {
  const KEY = 'kasago:chunk-reload-at'
  const last = Number(sessionStorage.getItem(KEY) ?? 0)
  if (Date.now() - last < 10_000) return
  sessionStorage.setItem(KEY, String(Date.now()))
  window.location.reload()
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min default; overridden per hook
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0, // booking mutations must never auto-retry
    },
  },
})

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
)
